import { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { PaymentElement } from 'react-stripe-js';
import { View } from 'foundations-components/components/View';
import { useNavMarker } from 'react-rhumb';
import { usePaymentFormContext } from '../../context/PaymentFormContext';
import usePageInteractionTracking from '../../hooks/usePageInteractionTracking';
import { INTERACTION_SECTIONS, INTERACTION_TARGETS } from 'payments-checkout-lib/constants/PageInteractionTracking';
import { ACH, AUTO, CARD, NEVER } from 'payments-checkout-lib/constants/PaymentElement';
import { hsToStripePaymentMethod, isBnpl, stripeToHSPaymentMethod } from 'payments-checkout-lib/utils/paymentMethods';
import { getBillingFieldSetting, getBillingNameSetting, getPaymentElementLayout } from 'payments-checkout-lib/utils/paymentElementOptions';
import { setErrorMessages, clearCardErrors, clearBankDebitErrors } from '../../state/errorMessagesSlice';
import { trackStripePaymentElementLoadError } from '../../utils/tracking';
import { getIsZeroCheckoutValid, isContractSetupCheckout } from '../../utils/validations';
import addGenericFloatingError from '../../utils/addGenericFloatingError';
import { jsx as _jsx } from "react/jsx-runtime";
const PAYMENT_METHOD_ORDER = [CARD, ACH].map(hsToStripePaymentMethod);
export const PaymentElementSection = ({
  resetSubmitError
}) => {
  var _checkoutSession$requ;
  const [ready, setReady] = useState(false);
  useNavMarker('PAYMENT_ELEMENT_LOADED', ready);
  const {
    checkoutSession,
    formState,
    dispatch
  } = usePaymentFormContext();
  const reduxDispatch = useDispatch();
  const paymentMethod = formState.paymentMethod;
  const isContractSetup = isContractSetupCheckout(checkoutSession);
  const isSetupIntent = isContractSetup || getIsZeroCheckoutValid(checkoutSession.subtotalsPublicResponse.paymentAmount, checkoutSession.lineItems);
  const stripePaymentMethod = formState.stripePaymentMethod;
  const setStripePaymentMethod = method => {
    dispatch({
      type: 'setStripePaymentMethod',
      data: {
        value: method
      }
    });
  };
  const {
    trackFirstFormFieldInteraction
  } = usePageInteractionTracking();
  const collectShippingAddress = checkoutSession.shippableCountries && !!checkoutSession.shippableCountries.length;
  const collectFullBillingAddress = checkoutSession.collectFullBillingAddress;
  const requireBillingAddressForACH = (_checkoutSession$requ = checkoutSession.requireBillingAddressForACH) !== null && _checkoutSession$requ !== void 0 ? _checkoutSession$requ : false;
  const onPaymentElementChange = changeData => {
    const newPaymentMethod = changeData.value.type;
    setStripePaymentMethod(newPaymentMethod);
    resetSubmitError();
    if (changeData.complete) {
      dispatch({
        type: 'resetStripeValidationError'
      });
      reduxDispatch(setErrorMessages({
        invalidStripePaymentMethod: {
          isError: false
        }
      }));
    } else {
      dispatch({
        type: 'setStripeValidationError'
      });
      reduxDispatch(setErrorMessages({
        invalidStripePaymentMethod: {
          isError: true
        }
      }));
    }
    if (stripePaymentMethod !== newPaymentMethod) {
      trackFirstFormFieldInteraction({
        section: INTERACTION_SECTIONS.paymentInfo,
        target: INTERACTION_TARGETS.paymentMethod
      });
      reduxDispatch(setErrorMessages({
        invalidStripePaymentMethod: {
          isError: false
        }
      }));

      // don't show card related errors if switching to bank debit payment method
      if (newPaymentMethod !== 'card') {
        reduxDispatch(clearCardErrors());
      } else {
        reduxDispatch(clearBankDebitErrors());
      }
      dispatch({
        type: 'setPaymentMethod',
        data: {
          value: stripeToHSPaymentMethod(newPaymentMethod)
        }
      });
    }
  };
  const billingFieldSetting = getBillingFieldSetting(collectFullBillingAddress, collectShippingAddress, paymentMethod, checkoutSession.isAutomatedSalesTaxEnabled, requireBillingAddressForACH);

  // line1/line2/city/state are NEVER only for ACH when the BillingAddressACH gate is open
  // (so HubSpot's own form is the sole collector). For CARD they must always be AUTO —
  // Stripe requires them even when requireBillingAddressForACH is true on the session.
  const addressLineFieldSetting = requireBillingAddressForACH && paymentMethod === ACH ? billingFieldSetting : AUTO;
  const paymentElementOptions = useMemo(() => ({
    fields: {
      billingDetails: {
        name: getBillingNameSetting(paymentMethod),
        email: NEVER,
        phone: AUTO,
        address: {
          line1: addressLineFieldSetting,
          line2: addressLineFieldSetting,
          city: addressLineFieldSetting,
          state: addressLineFieldSetting,
          country: billingFieldSetting,
          postalCode: billingFieldSetting
        }
      }
    },
    wallets: {
      applePay: isContractSetup ? NEVER : AUTO,
      googlePay: isContractSetup ? NEVER : AUTO
    },
    paymentMethodOrder: PAYMENT_METHOD_ORDER,
    layout: getPaymentElementLayout(checkoutSession.paymentMethods.map(pm => pm.paymentMethod).filter(method => !(isSetupIntent && isBnpl(method))), !isContractSetup),
    terms: {
      card: NEVER,
      usBankAccount: NEVER,
      sepaDebit: NEVER,
      applePay: NEVER,
      googlePay: NEVER,
      klarna: NEVER,
      affirm: NEVER
    }
  }), [paymentMethod, billingFieldSetting, addressLineFieldSetting, checkoutSession.paymentMethods, isContractSetup, isSetupIntent]);
  const handleLoadError = ({
    error
  }) => {
    trackStripePaymentElementLoadError({
      stripeErrorMessage: error === null || error === void 0 ? void 0 : error.message,
      stripeErrorCode: error === null || error === void 0 ? void 0 : error.code
    });
    addGenericFloatingError();
    dispatch({
      type: 'setPaymentElementLoadError'
    });
  };
  return /*#__PURE__*/_jsx(View, {
    "data-test-id": "preloaded-stripe-element-container",
    "data-ready": ready,
    marginBlockStart: "400",
    children: /*#__PURE__*/_jsx(PaymentElement, {
      id: "payment-info-input-container",
      onChange: onPaymentElementChange,
      onReady: () => setReady(true),
      options: paymentElementOptions,
      onLoadError: handleLoadError
    })
  });
};
export default PaymentElementSection;